import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ParticipantService from "../../../services/srv-participants/ParticipantService";
import LoadingPanel from "../../../components/sys-toolbox/LoadingPanel";
import {
  Chart,
  ChartSeries,
  ChartSeriesItem,
  ChartTitle,
  ChartLegend,
} from "@progress/kendo-react-charts";
import CommonGrid from "../../../components/sys-common/CommonGrid";
import { eventParticipantGridColumns } from "./EventParticipantGridColumns";
import { confirmAlert } from "react-confirm-alert";
import { getConfirmWarningMessage } from "../../../helpers/utils";
import { formatDate } from "@progress/kendo-intl";

function EventParticipantList(props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pieData, setPieData] = useState({
    participation: [],
  });
  const [dynamicColumns, setDynamicColumns] = useState([]);
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  const [dataState, setDataState] = useState({
    take: 10,
    skip: 0,
    filter: {
      logic: "and",
      filters: [
        {
          field: "etkinliK_TANIM_ID",
          operator: "eq",
          value: props.event.etkinliK_TANIM_ID,
        },
      ],
    },
    sort: [
      {
        field: "crE_DATE",
        dir: "desc",
      },
    ],
  });

  useEffect(() => {
    getParticipants();
    getParticipationStatistics();
  }, [dataState]);

  useEffect(() => {
    if (props.isAdded) {
      getParticipants();
      getParticipationStatistics();
      props.setIsAdded(false);
    }
  }, [props.isAdded]);

  function getParticipants() {
    setLoading(true);
    ParticipantService.getEventParticipants(dataState).then((resData) => {
      var data = resData.data;
      var dynamicColumns = [];
      var dynamicColumnsTypes = {};

      var newData = data.data.map((item) => {
        var ekAlanlar = {};
        if (item.eK_BILGILER != null) {
          ekAlanlar = JSON.parse(item.eK_BILGILER);

          Object.keys(ekAlanlar).forEach((key) => {
            dynamicColumns.push(key);

            const value = ekAlanlar[key];
            if (value !== null && value !== undefined) {
              const fieldConfig = t(`additional_fields.${key}`, {
                returnObjects: true,
              });

              if (
                fieldConfig &&
                typeof fieldConfig === "object" &&
                fieldConfig.text &&
                fieldConfig.datatype
              ) {
                if (fieldConfig.datatype.startsWith("date_formats.")) {
                  try {
                    const date = new Date(value);
                    if (!isNaN(date)) {
                      const dateFormatKey = fieldConfig.datatype;
                      ekAlanlar[key] = formatDate(date, t(dateFormatKey));
                    }
                  } catch (e) {
                    console.error(`Error formatting date for ${key}:`, e);
                  }
                } else if (fieldConfig.datatype === "text") {
                  ekAlanlar[key] = value;
                } else if (fieldConfig.datatype === "localize") {
                  ekAlanlar[key] = t(value);
                }
              } else {
                if (dynamicColumnsTypes[key] === "date") {
                  try {
                    const date = new Date(value);
                    if (!isNaN(date)) {
                      if (value.includes("T")) {
                        ekAlanlar[key] = formatDate(
                          date,
                          t("date_formats.longDateTime")
                        );
                      } else {
                        ekAlanlar[key] = formatDate(
                          date,
                          t("date_formats.shortDate")
                        );
                      }
                    }
                  } catch (e) {
                    console.error("Tarih formatlaması hata:", e);
                  }
                } else if (dynamicColumnsTypes[key] === "localize") {
                  ekAlanlar[key] = t(value);
                }
              }
            }
          });
        }
        return {
          ...item,
          ...ekAlanlar,
        };
      });
      setData({ ...data, data: newData });
      setDynamicColumns(dynamicColumns);
      setLoading(false);
    });
  }

  function getParticipationStatistics() {
    setStatisticsLoading(true);
    ParticipantService.getParticipationStatistics(props.event.etkinliK_TANIM_ID)
      .then((statistics) => {
        if (statistics.totalCount > 0) {
          setPieData({
            participation: [
              {
                category: t("will_participate"),
                value: statistics.willParticipate / statistics.totalCount,
                color: "#2979FF",
              },
              {
                category: t("will_not_participate"),
                value: statistics.willNotParticipate / statistics.totalCount,
                color: "#FF5252",
              },
              {
                category: t("tentative"),
                value: statistics.tentative / statistics.totalCount,
                color: "#fcc95dff",
              },
              {
                category: t("did_not_answer"),
                value: statistics.noAnswer / statistics.totalCount,
                color: "#9E9E9E",
              },
            ],
          });
        } else {
          setPieData({
            participation: [],
          });
        }
        setStatisticsLoading(false);
      })
      .catch((error) => {
        console.error("İstatistik yüklenirken hata:", error);
        setPieData({
          participation: [],
        });
        setStatisticsLoading(false);
      });
  }

  function deleteParticipant(propColumn) {
    const defaultAlertMessages = {
      TittleWarning: t("warning"),
      MessageDetailWarning: t("are_you_sure_for_delete"),
      Yes: t("yes"),
      No: t("no"),
    };
    confirmAlert({
      customUI: ({ onClose }) => {
        return getConfirmWarningMessage(
          onClose,
          () => {
            ParticipantService.deleteEventParticipant(
              propColumn.etkinliK_KATILIMCI_ID
            ).then(() => {
              props.setIsAdded(true);
            });
          },
          defaultAlertMessages
        );
      },
    });
  }

  const labelContent = (props) => {
    return `${props.dataItem.category} : ${Number(
      props.dataItem.value
    ).toLocaleString(undefined, {
      style: "percent",
      minimumFractionDigits: 2,
    })}`;
  };

  return (
    <>
      {loading && <LoadingPanel />}
      <CommonGrid
        style={{ marginTop: "20px" }}
        data={data}
        dataState={dataState}
        onDataStateChange={(event) => setDataState(event.dataState)}
        columns={eventParticipantGridColumns}
        dynamicColumns={dynamicColumns}
        addButton={{
          text: t("add_participant"),
          onClick: props.toggleDialogAddParticipant,
        }}
        deleteButton={{ text: t("delete"), onClick: deleteParticipant }}
        onAddClick={props.toggleDialogAddParticipant}
        exportExcelProps={{
          loadingFunction: setLoading,
          serviceMethod: ParticipantService.getParticipantsForExcel,
          fileName: "KatilimciListesi",
        }}
        dataItemKey="etkinliK_KATILIMCI_ID"
        extraToolbarButtons={[
          {
            text: t("add_participant_daikin"),
            icon: "fa fa-plus-circle",
            color: "primary",
            onClick: props.toggleDialogAddDaikinParticipant,
          },
          {
            text: t("send_mail"),
            icon: "fa fa-envelope",
            color: "info",
            onClick: props.toggleDialogSendMail,
          },
          {
            text: t("send_calendar_invitation"),
            icon: "fa fa-calendar-o",
            color: "info",
            onClick: props.toggleDialogSendInvitation,
          },
          {
            text: t("send_update_calendar_invitation"),
            icon: "fa fa-calendar-o",
            color: "info",
            onClick: props.toggleDialogSendUpdateInvitation,
          },
          {
            text: t("send_reminder_mail"),
            icon: "fa fa-envelope",
            color: "info",
            onClick: () => props.toggleDialogSendReminderMail(dataState),
          },
        ]}
      />

      {!statisticsLoading && pieData.participation.length > 0 && (
        <Chart>
          <ChartTitle text={t("participation_status")} />
          <ChartLegend position="bottom" />
          <ChartSeries>
            <ChartSeriesItem
              type="pie"
              data={pieData.participation}
              categoryField="category"
              field="value"
              colorField="color"
              labels={{ visible: true, content: labelContent }}
            />
          </ChartSeries>
        </Chart>
      )}
    </>
  );
}

export { EventParticipantList };
